import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const READ_TIMEOUT = 20000;
export const LONG_TIMEOUT = 90000;
const LONG = { timeout: LONG_TIMEOUT };

const http = axios.create({ baseURL: API, withCredentials: true, timeout: READ_TIMEOUT });

// Leituras (GET) ficam associadas à página atual e são canceladas ao navegar. Escritas nunca são canceladas.
let routeController = new AbortController();
export function resetRouteRequests() {
  routeController.abort();
  routeController = new AbortController();
}

http.interceptors.request.use((config) => {
  if ((config.method || "get").toLowerCase() === "get" && !config.signal) config.signal = routeController.signal;
  return config;
});

const RETURN_KEY = "mio:returnTo";
export function consumeReturnTo() {
  try {
    const v = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return v && v.startsWith("/") && !v.startsWith("/login") && !v.startsWith("/auth") ? v : "/";
  } catch { return "/"; }
}

export const isNetworkError = (err) => !err?.response && !axios.isCancel(err);
export const isNotFound = (err) => [403, 404, 410].includes(err?.response?.status);

let redirecting = false;
http.interceptors.response.use(
  (r) => { toast.dismiss("network-error"); return r; },
  (err) => {
    // Pedido de uma página que já foi abandonada: nunca resolve, para não mostrar erros fora de contexto.
    if (axios.isCancel(err)) return new Promise(() => {});
    if (err?.response?.status === 401) {
      const here = window.location.pathname;
      if (!here.startsWith("/login") && !here.startsWith("/auth/callback") && !here.startsWith("/portal/")) {
        if (!redirecting) {
          redirecting = true;
          let hasDraft = false;
          try {
            sessionStorage.setItem(RETURN_KEY, here + window.location.search);
            hasDraft = Object.keys(sessionStorage).some((k) => k.startsWith("mio:draft:"));
          } catch { /* */ }
          toast.warning(hasDraft
            ? "A sessão expirou. O rascunho foi guardado — entre novamente para continuar."
            : "A sessão expirou. Entre novamente para continuar.", { id: "session-expired" });
          setTimeout(() => { window.location.href = "/login"; }, 1200);
        }
        // Vai redirecionar para o login: não propagar o erro (evita toasts contraditórios nas páginas).
        return new Promise(() => {});
      }
    } else if (isNetworkError(err)) {
      const timeout = err.code === "ECONNABORTED" || err.code === "ETIMEDOUT";
      toast.error(timeout ? "O servidor demorou demasiado a responder. Tente novamente." : "Sem ligação ao servidor. Verifique a internet e tente novamente.", { id: "network-error" });
    }
    return Promise.reject(err);
  }
);

// Evita duplicação acidental (duplo clique): a mesma escrita em curso devolve a mesma promessa.
const inflight = new Map();
["post", "put", "patch", "delete"].forEach((m) => {
  const orig = http[m].bind(http);
  http[m] = (url, ...args) => {
    const body = m === "delete" ? "" : JSON.stringify(args[0] ?? null);
    const key = `${m}:${url}:${body}`;
    if (inflight.has(key)) return inflight.get(key);
    const p = orig(url, ...args).finally(() => inflight.delete(key));
    inflight.set(key, p);
    return p;
  };
});

export const Auth = {
  me: () => http.get("/auth/me").then(r => r.data),
  session: (session_id) => http.post("/auth/session", { session_id }).then(r => r.data),
  logout: () => http.post("/auth/logout").then(r => r.data),
  updateSettings: (data) => http.patch("/auth/settings", data).then(r => r.data),
};

export const Admin = {
  listUsers: () => http.get("/admin/users").then(r => r.data),
  toggleUser: (user_id, is_active) => http.patch(`/admin/users/${user_id}/active`, { is_active }).then(r => r.data),
  setTrial: (user_id, trial_until) => http.patch(`/admin/users/${user_id}/trial`, { trial_until }).then(r => r.data),
  listInvites: () => http.get("/admin/invites").then(r => r.data),
  createInvite: (email) => http.post("/admin/invites", { email }).then(r => r.data),
  revokeInvite: (token) => http.delete(`/admin/invites/${token}`).then(r => r.data),
};

export const Invites = {
  info: (token) => http.get(`/auth/invite/${token}`).then(r => r.data),
};

export const Integrations = {
  me: () => http.get("/integrations/me").then(r => r.data),
  setTwilio: (payload) => http.post("/integrations/twilio", payload).then(r => r.data),
  clearTwilio: () => http.delete("/integrations/twilio").then(r => r.data),
  testSms: (to, message) => http.post("/integrations/test-sms", { to, message }).then(r => r.data),
  emailStatus: () => http.get("/integrations/email/status").then(r => r.data),
  testEmail: () => http.post("/integrations/test-email").then(r => r.data),
};

export const Library = {
  list: (params = {}) => http.get("/library", { params }).then(r => r.data),
  create: (data) => http.post("/library", data).then(r => r.data),
  update: (id, data) => http.patch(`/library/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/library/${id}`).then(r => r.data),
};

export const Matrix = {
  demos: (itemId, all = false) => http.get(`/matrix/items/${itemId}/demos`, { params: all ? { all: true } : {} }).then(r => r.data),
  addDemo: (itemId, data) => http.post(`/matrix/items/${itemId}/demos`, data).then(r => r.data),
  review: (demoId, data) => http.post(`/matrix/demos/${demoId}/review`, data).then(r => r.data),
  patientVisible: (demoId, visible) => http.post(`/matrix/demos/${demoId}/patient-visible`, { visible }).then(r => r.data),
};

export const Studio = {
  list: () => http.get("/studio/components").then(r => r.data),
  get: (id) => http.get(`/studio/components/${id}`).then(r => r.data),
  validate: (svg) => http.post("/studio/validate", { svg }).then(r => r.data),
  create: (data) => http.post("/studio/components", data).then(r => r.data),
  pilot: (key) => http.post(key ? `/studio/components/pilot/${key}` : "/studio/components/pilot").then(r => r.data),
  save: (id, data) => http.put(`/studio/components/${id}`, data).then(r => r.data),
  status: (id, status) => http.post(`/studio/components/${id}/status`, { status }).then(r => r.data),
  archive: (id, archived) => http.post(`/studio/components/${id}/archive`, { archived }).then(r => r.data),
  duplicate: (id) => http.post(`/studio/components/${id}/duplicate`).then(r => r.data),
  restore: (id, v) => http.post(`/studio/components/${id}/restore/${v}`).then(r => r.data),
  drafts: () => http.get("/studio/exercise-drafts").then(r => r.data),
  applyDraft: (id) => http.post(`/studio/exercise-drafts/${id}/apply`).then(r => r.data),
};

export const Billing = {
  plans: () => http.get("/billing/plans").then(r => r.data),
  checkout: (plan) => http.post("/billing/checkout", { plan, origin_url: window.location.origin }).then(r => r.data),
  status: (session_id) => http.get(`/billing/status/${session_id}`).then(r => r.data),
};

export const Patients = {
  list: (params) => http.get("/patients", { params }).then(r => r.data),
  get: (id) => http.get(`/patients/${id}`).then(r => r.data),
  create: (data) => http.post("/patients", data).then(r => r.data),
  update: (id, data) => http.put(`/patients/${id}`, data).then(r => r.data),
  intake: (id, data) => http.patch(`/patients/${id}/intake`, data).then(r => r.data),
  setActive: (id, patient_active) => http.patch(`/patients/${id}/active`, { patient_active }).then(r => r.data),
  remove: (id) => http.delete(`/patients/${id}`).then(r => r.data),
  assessments: (id, params) => http.get(`/patients/${id}/assessments`, { params }).then(r => r.data),
  compare: (id, a, b) => http.get(`/patients/${id}/compare`, { params: { a, b } }).then(r => r.data),
};

export const Assessments = {
  create: (data) => http.post("/assessments", data, LONG).then(r => r.data),
  get: (id) => http.get(`/assessments/${id}`).then(r => r.data),
  detect: (id) => http.post(`/assessments/${id}/detect-landmarks`, null, LONG).then(r => r.data),
  saveLandmarks: (id, data) => http.put(`/assessments/${id}/landmarks`, data).then(r => r.data),
  sessionContext: (id) => http.get(`/assessments/${id}/session-context`).then(r => r.data),
  setSessionContext: (id, data) => http.put(`/assessments/${id}/session-context`, data).then(r => r.data),
  patientSummary: (id) => http.get(`/assessments/${id}/patient-summary`).then(r => r.data),
  savePatientSummary: (id, data) => http.put(`/assessments/${id}/patient-summary`, data).then(r => r.data),
  analyze: (id) => http.post(`/assessments/${id}/analyze`, null, LONG).then(r => r.data),
  updateTreatmentPlan: (id, treatment_plan) => http.put(`/assessments/${id}/treatment-plan`, { treatment_plan }).then(r => r.data),
  setVas: (id, vas_score) => http.put(`/assessments/${id}/vas`, { vas_score }).then(r => r.data),
  setGrid: (id, settings) => http.patch(`/assessments/${id}/grid`, settings).then(r => r.data),
  generateAppointments: (id) => http.post(`/assessments/${id}/generate-appointments`, null, LONG).then(r => r.data),
  shareToken: (id) => http.get(`/assessments/${id}/share-token`).then(r => r.data),
  setShare: (id, days) => http.post(`/assessments/${id}/share`, { days }).then(r => r.data),
  revokeShare: (id) => http.post(`/assessments/${id}/share/revoke`).then(r => r.data),
  pdfUrl: (id) => `${API}/assessments/${id}/report.pdf`,
};

export const Appointments = {
  list: (params) => http.get("/appointments", { params }).then(r => r.data),
  create: (data) => http.post("/appointments", data).then(r => r.data),
  update: (id, data) => http.put(`/appointments/${id}`, data).then(r => r.data),
  remove: (id) => http.delete(`/appointments/${id}`).then(r => r.data),
};

export const Reminders = {
  pending: () => http.get("/reminders/pending").then(r => r.data),
  send: (id) => http.post(`/reminders/send/${id}`).then(r => r.data),
};

export const Stats = {
  get: () => http.get("/stats").then(r => r.data),
};

export const Overview = {
  get: () => http.get("/overview").then(r => r.data),
};

export const Reports = {
  summary: () => http.get("/reports/summary").then(r => r.data),
};

export const Sign = {
  templates: () => http.get("/sign/templates").then(r => r.data),
  list: (patient_id) => http.get("/sign/requests", { params: { patient_id } }).then(r => r.data),
  create: (data) => http.post("/sign/requests", data).then(r => r.data),
  revoke: (id) => http.post(`/sign/requests/${id}/revoke`).then(r => r.data),
  document: (id) => http.get(`/sign/requests/${id}/document`).then(r => r.data),
  sign: (id, data) => http.post(`/sign/requests/${id}/sign`, data).then(r => r.data),
  receiptUrl: (recId) => `${API}/sign/records/${recId}/receipt.pdf`,
  status: (patient_id) => http.get("/sign/status", { params: { patient_id } }).then(r => r.data),
  withdraw: (recId, choice_key) => http.post(`/sign/records/${recId}/withdraw`, { choice_key }).then(r => r.data),
  check: (patient_id, op) => http.get("/sign/check", { params: { patient_id, op } }).then(r => r.data),
  history: (patient_id) => http.get("/sign/history", { params: { patient_id } }).then(r => r.data),
  exportUrl: (patient_id) => `${API}/sign/export.pdf?patient_id=${encodeURIComponent(patient_id)}`,
  rules: () => http.get("/sign/rules").then(r => r.data),
  setRules: (data) => http.put("/sign/rules", data).then(r => r.data),
};

// Paginação por cursor: pede PAGE+1 para saber se existe página seguinte.
export async function fetchPage(fn, page, cursor, extra = {}) {
  const rows = await fn({ limit: page + 1, cursor: cursor || undefined, ...extra });
  return { items: rows.slice(0, page), more: rows.length > page };
}
export const cursorOf = (row) => (row ? `${row.created_at}|${row.id}` : null);
export const mergeById = (prev, items) => {
  const seen = new Set(prev.map((x) => x.id));
  return [...prev, ...items.filter((x) => !seen.has(x.id))];
};
